/**
 * Right-side-panel route search contract.
 *
 * The chat thread route surfaces a single right-side panel at a time
 * (Preview, Canvas/Design, or Diff). Earlier iterations modeled each surface
 * as an independent boolean flag (`?preview=1`, `?canvas=1`, `?diff=1`),
 * which let invalid combinations slip into the URL even though the renderer
 * always picked one. This module is the single source of truth for the
 * canonical `?rightPanel=preview|canvas|diff` contract, plus per-pane
 * sub-state (`diffTurnId`, `diffFilePath`, `previewTabId`).
 *
 * Legacy boolean flags are still accepted on the way IN (so shared bookmarks
 * keep working) but are never written back OUT.
 */
import { TurnId } from "@cmmd-forge/contracts";

import { RIGHT_PANEL_KINDS, type RightPanelKind } from "./rightPanelSurface";

/**
 * The surface kinds a URL is allowed to name.
 *
 * Derived from `RIGHT_PANEL_KINDS` rather than restated, so the route contract
 * and the surface model cannot drift apart — they were two hand-maintained
 * lists before, and the drift was silent.
 *
 * `file` is excluded because a file surface is addressed by path, which this
 * contract has no key for. No link can ask for one.
 */
export type RightPanelId = Exclude<RightPanelKind, "file" | "pull-request">;

const ROUTE_LINKABLE_PANEL_IDS: readonly RightPanelId[] = RIGHT_PANEL_KINDS.filter(
  (kind): kind is RightPanelId => kind !== "file" && kind !== "pull-request",
);

export interface DiffRouteSearch {
  rightPanelOpen?: boolean | undefined;
  rightPanel?: RightPanelId | undefined;
  rightPanelTabs?: ReadonlyArray<RightPanelId> | undefined;
  diffTurnId?: TurnId | undefined;
  diffFilePath?: string | undefined;
  previewTabId?: string | undefined;
}

function isLegacyOpenValue(value: unknown): boolean {
  return value === "1" || value === 1 || value === true;
}

function isLegacyClosedValue(value: unknown): boolean {
  return value === "0" || value === 0 || value === false;
}

function normalizeSearchString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function isRightPanelId(value: unknown): value is RightPanelId {
  return (
    typeof value === "string" && (ROUTE_LINKABLE_PANEL_IDS as readonly string[]).includes(value)
  );
}

function uniqueRightPanelIds(values: ReadonlyArray<RightPanelId>): RightPanelId[] {
  const seen = new Set<RightPanelId>();
  const next: RightPanelId[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    next.push(value);
  }
  return next;
}

function parseRightPanelTabs(value: unknown): RightPanelId[] {
  if (Array.isArray(value)) {
    return uniqueRightPanelIds(value.filter(isRightPanelId));
  }
  if (typeof value !== "string") {
    return [];
  }
  const normalized = value.trim();
  if (normalized.startsWith("[") && normalized.endsWith("]")) {
    try {
      const parsed = JSON.parse(normalized) as unknown;
      return Array.isArray(parsed) ? uniqueRightPanelIds(parsed.filter(isRightPanelId)) : [];
    } catch {
      return [];
    }
  }
  return uniqueRightPanelIds(
    normalized
      .split(",")
      .map((entry) => entry.trim())
      .filter(isRightPanelId),
  );
}

/**
 * Legacy boolean flags resolve to `rightPanel` with the same priority the
 * pre-consolidation renderer used (canvas > preview > diff). This guarantees
 * that links shared from the old contract keep showing the same surface.
 */
function inferLegacyRightPanel(search: Record<string, unknown>): RightPanelId | undefined {
  if (isLegacyOpenValue(search["canvas"])) return "canvas";
  if (isLegacyOpenValue(search["preview"])) return "preview";
  if (isLegacyOpenValue(search["diff"])) return "diff";
  return undefined;
}

/**
 * Whether a link says the panel is open, and with which tabs.
 *
 * `rightPanelOpen` is tri-state on the way in: explicitly open, explicitly
 * closed, or unstated — and unstated means "open if the link named tabs",
 * because a link that lists tabs and no open flag came from a shared panel.
 */
function parseRightPanelVisibility(
  search: Record<string, unknown>,
  rightPanel: RightPanelId | undefined,
): {
  readonly rightPanelOpen: boolean;
  readonly rightPanelTabs: readonly RightPanelId[];
  readonly stated: boolean;
  readonly hasExplicitTabs: boolean;
} {
  const parsedTabs = parseRightPanelTabs(search["rightPanelTabs"]);
  const rightPanelTabs = uniqueRightPanelIds(
    rightPanel && !parsedTabs.includes(rightPanel) ? [...parsedTabs, rightPanel] : parsedTabs,
  );
  const hasExplicitTabs = normalizeSearchString(search["rightPanelTabs"]) !== undefined;
  const open = isLegacyOpenValue(search["rightPanelOpen"]);
  const closed = isLegacyClosedValue(search["rightPanelOpen"]);

  return {
    rightPanelOpen: open ? true : closed ? false : hasExplicitTabs,
    rightPanelTabs,
    stated: open || closed || hasExplicitTabs,
    hasExplicitTabs,
  };
}

/**
 * The payload keys, which only mean anything to the panel that owns them.
 *
 * Read per kind rather than unconditionally: a `diffTurnId` left over from a
 * previous panel must not survive a link that now names `preview`.
 */
function parseRightPanelPayload(
  search: Record<string, unknown>,
  rightPanel: RightPanelId | undefined,
): Pick<DiffRouteSearch, "diffTurnId" | "diffFilePath" | "previewTabId"> {
  if (rightPanel === "preview") {
    const previewTabId = normalizeSearchString(search["previewTabId"]);
    return previewTabId ? { previewTabId } : {};
  }
  if (rightPanel !== "diff") return {};

  const turnIdRaw = normalizeSearchString(search["diffTurnId"]);
  if (!turnIdRaw) return {};
  const diffTurnId = TurnId.make(turnIdRaw);
  const diffFilePath = normalizeSearchString(search["diffFilePath"]);
  return diffFilePath ? { diffTurnId, diffFilePath } : { diffTurnId };
}

export function parseDiffRouteSearch(search: Record<string, unknown>): DiffRouteSearch {
  const explicit = isRightPanelId(search["rightPanel"])
    ? (search["rightPanel"] as RightPanelId)
    : undefined;
  const rightPanel = explicit ?? inferLegacyRightPanel(search);
  const visibility = parseRightPanelVisibility(search, rightPanel);

  return {
    ...(visibility.stated ? { rightPanelOpen: visibility.rightPanelOpen } : {}),
    ...(rightPanel ? { rightPanel } : {}),
    ...(visibility.hasExplicitTabs && visibility.rightPanelTabs.length > 0
      ? { rightPanelTabs: visibility.rightPanelTabs }
      : {}),
    ...parseRightPanelPayload(search, rightPanel),
  };
}
